import React, { useEffect, useState, useCallback } from "react";
import useContract from "../hooks/useContract";
import { toast } from "react-hot-toast";
import { Contract, formatUnits, JsonRpcProvider } from "ethers";

const RPC_URL = "https://bsc-dataseed.binance.org";

const KIND_ADDRESS = "0x41f52A42091A6B2146561bF05b722Ad1d0e46f8b";
const KIND_BNB_PAIR = "0x628C0fe6DA9854EDf0f3E2C7657af6ecf29C740E";

const Withdraw = () => {
  /*
   * AppKit removed from this page.
   * Wallet information now comes from useContract().
   */
  const {
    address,
    isConnected,
    getUserDashboard,
    getClaimable,
    withdraw,
  } = useContract();

  const [data, setData] = useState({
    energyWalletSettled: "0",
    referralWalletSettled: "0",
    totalEnergyClaimable: "0",
    totalReferralClaimable: "0",
    totalClaimableUSD: "0",
    totalWithdrawnUsd: "0",
    activeCards: 0,
    totalPaidCards: 0,
    rank: 0,
    nextWithdrawTime: 0,
  });

  const [claimLoading, setClaimLoading] = useState(false);
  const [kindPrice, setKindPrice] = useState(0);
  const [kindTokenAmount, setKindTokenAmount] = useState("0");
  const [refreshing, setRefreshing] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  /*
   * Read-only BSC provider.
   * This is only used for KIND price.
   */
  const provider = new JsonRpcProvider(RPC_URL);

  // =========================================================
  // FORMAT HELPERS
  // =========================================================

  const formatNumber = (value, decimals = 4) => {
    const number = Number(value || 0);

    if (!Number.isFinite(number)) {
      return "0";
    }

    return number.toFixed(decimals);
  };

  const formatTime = (seconds) => {
    const totalSeconds = Math.max(0, Number(seconds || 0));

    const days = Math.floor(totalSeconds / 86400);
    const hrs = Math.floor((totalSeconds % 86400) / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;

    return `${days}d ${hrs}h ${mins}m ${secs}s`;
  };

  // =========================================================
  // KIND PRICE
  // =========================================================

  const fetchKindPrice = useCallback(async () => {
    try {
      const pair = new Contract(
        KIND_BNB_PAIR,
        [
          "function getReserves() view returns (uint112 reserve0, uint112 reserve1, uint32 blockTimestampLast)",
          "function token0() view returns (address)",
          "function token1() view returns (address)",
        ],
        provider
      );

      const [reserves, token0] = await Promise.all([
        pair.getReserves(),
        pair.token0(),
      ]);

      let kindReserve;
      let bnbReserve;

      if (token0.toLowerCase() === KIND_ADDRESS.toLowerCase()) {
        kindReserve = reserves[0];
        bnbReserve = reserves[1];
      } else {
        kindReserve = reserves[1];
        bnbReserve = reserves[0];
      }

      const kindAmount = Number(formatUnits(kindReserve, 18));
      const bnbAmount = Number(formatUnits(bnbReserve, 18));

      if (
        !Number.isFinite(kindAmount) ||
        !Number.isFinite(bnbAmount) ||
        kindAmount <= 0 ||
        bnbAmount <= 0
      ) {
        return;
      }

      const kindPriceInBNB = bnbAmount / kindAmount;

      try {
        const response = await fetch(
          "https://api.binance.com/api/v3/ticker/price?symbol=BNBUSDT"
        );

        if (!response.ok) {
          throw new Error("Failed to fetch BNB price");
        }

        const bnbData = await response.json();

        const bnbUsdPrice = parseFloat(bnbData.price);

        if (!Number.isFinite(bnbUsdPrice) || bnbUsdPrice <= 0) {
          return;
        }

        const kindUsdPrice = kindPriceInBNB * bnbUsdPrice;

        if (Number.isFinite(kindUsdPrice) && kindUsdPrice > 0) {
          setKindPrice(kindUsdPrice);
        }
      } catch (priceError) {
        console.error("BNB USD price error:", priceError);
      }
    } catch (err) {
      console.error("KIND price fetch failed:", err);
    }
  }, [provider]);

  // =========================================================
  // REFRESH WITHDRAW DATA
  // =========================================================

  const refreshWithdrawData = useCallback(async () => {
    if (!isConnected || !address) {
      return;
    }

    try {
      setRefreshing(true);

      const [dashboardRes, claimableRes] = await Promise.all([
        getUserDashboard(address),
        getClaimable(address),
      ]);

      const energyWalletSettled = parseFloat(
        dashboardRes?.energyWallet || "0"
      );

      const referralWalletSettled = parseFloat(
        dashboardRes?.referralWallet || "0"
      );

      const totalEnergyClaimable = parseFloat(
        claimableRes?.energy || "0"
      );

      const totalReferralClaimable = parseFloat(
        claimableRes?.referral || "0"
      );

      /*
       * IMPORTANT:
       *
       * Total withdrawable should come from BOTH
       * energy + referral claimable amounts.
       */
      const totalUSD =
        totalEnergyClaimable + totalReferralClaimable;

      const kindAmount =
        kindPrice > 0 ? totalUSD / kindPrice : 0;

      const now = Math.floor(Date.now() / 1000);

      const cooldownEnd = Number(
        dashboardRes?.nextWithdrawTime || 0
      );

      const remaining = Math.max(
        0,
        cooldownEnd - now
      );

      setData({
        energyWalletSettled: formatNumber(
          energyWalletSettled,
          4
        ),

        referralWalletSettled: formatNumber(
          referralWalletSettled,
          4
        ),

        totalEnergyClaimable: formatNumber(
          totalEnergyClaimable,
          4
        ),

        totalReferralClaimable: formatNumber(
          totalReferralClaimable,
          4
        ),

        totalClaimableUSD: totalUSD.toLocaleString(
          "en-US",
          {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }
        ),

        totalWithdrawnUsd: formatNumber(
          dashboardRes?.totalWithdrawnUsd || 0,
          2
        ),

        activeCards: dashboardRes?.activeCards || 0,

        totalPaidCards:
          dashboardRes?.totalPaidCards || 0,

        rank: dashboardRes?.rank || 0,

        nextWithdrawTime: cooldownEnd,
      });

      setKindTokenAmount(
        kindAmount.toFixed(6)
      );

      setCooldownRemaining(remaining);
    } catch (err) {
      console.error(
        "Refresh withdraw data error:",
        err
      );

      toast.error(
        "Failed to load withdrawal data"
      );
    } finally {
      setRefreshing(false);
    }
  }, [
    isConnected,
    address,
    getUserDashboard,
    getClaimable,
    kindPrice,
  ]);

  // =========================================================
  // WITHDRAW
  // =========================================================



  const BSC_CHAIN_ID = "0x38"; // 56 in hex
const BSC_CHAIN_ID_DEC = 56;

const switchToBSC = async () => {
  if (!window.ethereum) {
    throw new Error("No crypto wallet found");
  }

  try {
    const chainId = await window.ethereum.request({ method: "eth_chainId" });

    if (chainId === BSC_CHAIN_ID) {
      // Already on BSC
      return true;
    }

    await window.ethereum.request({
      method: "wallet_switchEthereumChain",
      params: [{ chainId: BSC_CHAIN_ID }],
    });

    return true;
  } catch (switchError) {
    // 4902 = chain not added, 4903 = user rejected, etc.
    if (switchError.code === 4902) {
      // Optionally add BSC network first
      await window.ethereum.request({
        method: "wallet_addEthereumChain",
        params: [
          {
            chainId: BSC_CHAIN_ID,
            chainName: "BNB Smart Chain",
            nativeCurrency: {
              name: "BNB",
              symbol: "BNB",
              decimals: 18,
            },
            rpcUrls: ["https://bsc-dataseed.binance.org"],
            blockExplorerUrls: ["https://bscscan.com"],
          },
        ],
      });

      // After adding, try switching again
      await window.ethereum.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: BSC_CHAIN_ID }],
      });

      return true;
    }

    // User rejected or other error
    throw switchError;
  }
};


const handleWithdraw = async () => {
  if (!address) {
    toast.error("Wallet not connected");
    return;
  }

  try {
    setClaimLoading(true);

    // 1️⃣ Switch to BSC first
    toast.loading("Switching to BSC network...", {
      id: "withdraw",
    });

    await switchToBSC();

    // Small delay to let wallet update chain (optional but often helpful)
    await new Promise((res) => setTimeout(res, 300));

    // 2️⃣ Now trigger withdrawal (this will show the confirmation popup)
    toast.loading("Please confirm the withdrawal in your wallet...", {
      id: "withdraw",
    });

    const txHash = await withdraw();

    console.log("Withdrawal TX:", txHash);

    toast.success("✅ Withdrawal successful!", {
      id: "withdraw",
    });
  } catch (err) {
    console.error("Withdraw Error:", err);

    // Don't show "Withdrawal failed" if user rejected network switch or tx
    if (err?.code === 4001 || err?.code === 4903) {
      toast.error("Action cancelled", {
        id: "withdraw",
        duration: 4000,
      });
      return;
    }

    const errorMessage =
      err?.reason ||
      err?.shortMessage ||
      err?.info?.error?.message ||
      err?.error?.message ||
      err?.data?.message ||
      err?.message ||
      "Withdrawal failed";

    toast.error(errorMessage, {
      id: "withdraw",
      duration: 8000,
    });
  } finally {
    setClaimLoading(false);
  }
};

  // =========================================================
  // COOLDOWN TIMER
  // =========================================================

  useEffect(() => {
    if (cooldownRemaining <= 0) {
      return;
    }

    const interval = setInterval(() => {
      setCooldownRemaining((prev) =>
        Math.max(0, prev - 1)
      );
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [cooldownRemaining]);

  // =========================================================
  // KIND PRICE REFRESH
  // =========================================================

  useEffect(() => {
    fetchKindPrice();

    const priceInterval = setInterval(
      fetchKindPrice,
      15000
    );

    return () => {
      clearInterval(priceInterval);
    };
  }, [fetchKindPrice]);

  // =========================================================
  // WITHDRAW DATA REFRESH
  // =========================================================

  useEffect(() => {
    if (!isConnected || !address) {
      return;
    }

    refreshWithdrawData();

    const interval = setInterval(
      refreshWithdrawData,
      30000
    );

    return () => {
      clearInterval(interval);
    };
  }, [
    isConnected,
    address,
    refreshWithdrawData,
  ]);

  // =========================================================
  // NOT CONNECTED
  // =========================================================

  /*
   * AppKit connection button has been removed.
   *
   * The wallet connection should now happen from your
   * global/header wallet button.
   */
  if (!isConnected || !address) {
    return (
      <div className="flex items-center justify-center min-h-[80vh] bg-[#111111] px-4">
        <div className="w-full max-w-sm sm:max-w-md md:max-w-lg bg-[#171717] border border-[#2A2A2A] backdrop-blur-xl rounded-3xl p-6 sm:p-8 md:p-10 text-center shadow-2xl">

          <div className="w-24 h-24 mx-auto mb-8 bg-[#D3AF37]/10 border border-[#D3AF37]/30 p-6 rounded-3xl flex items-center justify-center shadow-2xl">
            <div className="text-4xl">
              💸
            </div>
          </div>

          <h2 className="text-3xl md:text-4xl font-bold text-[#D3AF37] mb-6">
            Wallet Not Connected
          </h2>

          <p className="text-gray-300 mb-4 text-lg">
            Please connect your wallet using the
            wallet button in the header.
          </p>

          <p className="text-gray-500 text-sm">
            After connecting, return to this page
            to view your earnings.
          </p>
        </div>
      </div>
    );
  }

  // =========================================================
  // MAIN PAGE
  // =========================================================

  return (
    <div className="min-h-screen p-4 md:p-6 text-white">

      {/* =====================================================
          HEADER
      ===================================================== */}

      <section className="mb-12 max-w-6xl mx-auto">
        <div className="bg-[#171717] border border-[#2A2A2A] p-8 lg:p-12 xl:p-16 rounded-3xl shadow-2xl">

          <div className="flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between">

            <div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#D3AF37] mb-3">
                💸 Withdraw Earnings
              </h1>

              <div className="flex flex-wrap items-center gap-3 text-sm bg-[#D3AF37]/10 p-3 rounded-xl border border-[#D3AF37]/20">

                <div className="w-3 h-3 bg-[#D3AF37] rounded-full animate-pulse" />

                <span className="font-mono">
                  {address.slice(0, 6)}...
                  {address.slice(-4)}
                </span>

                <span className="text-[#D3AF37]">
                  Rank {data.rank}
                </span>

                <span className="text-gray-400">
                  •
                </span>

                <span className="text-[#D3AF37]">
                  {data.activeCards} Active Cards
                </span>

              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">

              <div className="text-[#D3AF37] text-lg font-mono bg-[#D3AF37]/10 px-4 py-3 rounded-xl min-w-[170px] text-center border border-[#D3AF37]/20">

                KIND: $
                {kindPrice > 0
                  ? kindPrice.toFixed(6)
                  : "Loading..."}

              </div>

              <button
                onClick={refreshWithdrawData}
                disabled={refreshing}
                className="px-5 py-3 rounded-xl bg-[#111111] border border-[#2A2A2A] hover:border-[#D3AF37]/50 transition-all disabled:opacity-50"
              >
                {refreshing ? (
                  <span className="inline-block animate-spin">
                    ⟳
                  </span>
                ) : (
                  "↻"
                )}

                <span className="ml-2">
                  Refresh
                </span>
              </button>

            </div>

          </div>
        </div>
      </section>

      {/* =====================================================
          TOTAL WITHDRAWABLE
      ===================================================== */}

      <section className="mb-16 max-w-6xl mx-auto">

        <div className="bg-[#171717] border border-[#2A2A2A] backdrop-blur-xl rounded-3xl p-8 lg:p-12 xl:p-16 text-center shadow-2xl">

          <div className="mb-10">

            <h3 className="text-2xl md:text-3xl font-bold text-[#D3AF37] mb-6 flex items-center justify-center gap-3">
              💵 Total Withdrawable
            </h3>

            <div className="space-y-3">

              <div className="text-5xl md:text-7xl lg:text-8xl font-black text-[#D3AF37]">
                ${data.totalClaimableUSD}
              </div>

              <div className="text-3xl md:text-4xl font-bold text-white">
                {Number(
                  kindTokenAmount || 0
                ).toLocaleString()}{" "}
                KIND
              </div>

              <div className="text-xl md:text-2xl text-[#D3AF37] flex items-center justify-center gap-2 flex-wrap">

                @ $
                {kindPrice > 0
                  ? kindPrice.toFixed(6)
                  : "0.000000"}{" "}
                KIND/USD

                {cooldownRemaining > 0 && (
                  <div className="ml-4 px-4 py-2 bg-[#D3AF37]/10 border border-[#D3AF37]/30 rounded-xl text-[#D3AF37] text-sm font-mono animate-pulse">
                    ⏱️{" "}
                    {formatTime(
                      cooldownRemaining
                    )}
                  </div>
                )}

              </div>

            </div>
          </div>

          {/* =================================================
              BREAKDOWN
          ================================================= */}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">

            {/* ENERGY */}

            <div className="bg-[#111111] p-6 sm:p-8 rounded-2xl border border-[#D3AF37]/20 hover:border-[#D3AF37]/40 transition-all">

              <h4 className="text-xl font-bold text-[#D3AF37] mb-4 flex items-center gap-2">
                ⚡ Energy Rewards
              </h4>

              <div className="space-y-3 text-lg">

                <div>
                  Settled:{" "}
                  <span className="text-white">
                    $
                    {data.energyWalletSettled}
                  </span>
                </div>

                <div>
                  Claimable:{" "}
                  <span className="text-[#D3AF37] font-semibold">
                    $
                    {data.totalEnergyClaimable}
                  </span>
                </div>

                <div className="text-sm text-[#B8B8B8]">

                  Pending: $

                  {Math.max(
                    0,
                    parseFloat(
                      data.totalEnergyClaimable
                    ) -
                      parseFloat(
                        data.energyWalletSettled
                      )
                  ).toFixed(4)}

                </div>

              </div>
            </div>

            {/* REFERRAL */}

            <div className="bg-[#111111] p-6 sm:p-8 rounded-2xl border border-[#D3AF37]/20 hover:border-[#D3AF37]/40 transition-all">

              <h4 className="text-xl font-bold text-[#D3AF37] mb-4 flex items-center gap-2">
                👥 Referral Rewards
              </h4>

              <div className="space-y-3 text-lg">

                <div>
                  Balance:{" "}
                  <span className="text-white">
                    $
                    {data.referralWalletSettled}
                  </span>
                </div>

                <div>
                  Claimable:{" "}
                  <span className="text-[#D3AF37] font-semibold">
                    $
                    {data.totalReferralClaimable}
                  </span>
                </div>

              </div>
            </div>

            {/* LIFETIME */}

            <div className="bg-[#111111] p-6 sm:p-8 rounded-2xl border border-[#D3AF37]/20 hover:border-[#D3AF37]/40 transition-all">

              <h4 className="text-xl font-bold text-[#D3AF37] mb-4 flex items-center gap-2">
                💎 Lifetime Stats
              </h4>

              <div className="space-y-3 text-lg">

                <div>
                  Total Withdrawn:{" "}
                  <span className="text-white">
                    $
                    {data.totalWithdrawnUsd}
                  </span>
                </div>

                <div>
                  Cards Bought:{" "}
                  <span className="text-[#D3AF37]">
                    {data.totalPaidCards}
                  </span>
                </div>

                <div>
                  Active Cards:{" "}
                  <span className="text-[#D3AF37]">
                    {data.activeCards}
                  </span>
                </div>

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =====================================================
          WITHDRAW BUTTON
      ===================================================== */}

      <section className="max-w-md mx-auto pb-12">

        <button
          onClick={handleWithdraw}
          disabled={
            claimLoading ||
            parseFloat(
              data.totalClaimableUSD || "0"
            ) <= 0 ||
            cooldownRemaining > 0
          }
          className={`w-full px-8 sm:px-10 md:px-12 py-8 sm:py-9 md:py-10 text-xl sm:text-2xl md:text-3xl font-black rounded-3xl shadow-2xl hover:shadow-3xl transition-all duration-500 border-4 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed group ${
            parseFloat(
              data.totalClaimableUSD || "0"
            ) <= 0 ||
            cooldownRemaining > 0
              ? "bg-[#171717] border-[#2A2A2A] text-gray-500 hover:bg-[#1A1A1A]"
              : "bg-[#D3AF37] hover:bg-[#B59410] border-[#D3AF37] text-white"
          }`}
        >

          {claimLoading ? (
            <>
              <div className="w-10 h-10 border-4 border-white border-t-transparent rounded-full animate-spin inline-block mr-6 mb-2" />

              <div className="text-xl">
                Processing...
              </div>
            </>
          ) : parseFloat(
              data.totalClaimableUSD || "0"
            ) <= 0 ? (
            "Nothing to Withdraw"
          ) : cooldownRemaining > 0 ? (
            `Wait ${formatTime(
              cooldownRemaining
            )}`
          ) : (
            `💸 Withdraw $${data.totalClaimableUSD}`
          )}

        </button>

      </section>

    </div>
  );
};

export default Withdraw;